/**
 * Si le backend n’a pas encore été redéployé avec `explore_chart`, le catalogue
 * `/institution/analytics/bi/catalog` ne contient pas le jeu « graphique personnalisé ».
 * On fusionne la même entrée que le serveur pour que l’UI reste utilisable après mise à jour front seule.
 * (Les appels createWidget / data exigent tout de même un backend à jour.)
 */

const ENROLLMENT_TABLE_COLUMNS = [
  'participantId',
  'name',
  'email',
  'groups',
  'flowId',
  'flowTitle',
  'completed',
  'startedAt',
  'completedAt',
  'lastAccessAt',
  'totalTimeSpentSeconds',
] as const;

const EXPLORE_SOURCE_KEYS = ['enrollments_detail', 'groups_overview', 'flows_overview', 'webinars_list'] as const;

const EXPLORE_DIMS: Record<(typeof EXPLORE_SOURCE_KEYS)[number], readonly string[]> = {
  enrollments_detail: [...ENROLLMENT_TABLE_COLUMNS],
  groups_overview: ['groupId', 'name', 'description', 'membersCount'],
  flows_overview: ['flowId', 'title', 'status', 'enrollments'],
  webinars_list: ['id', 'title', 'status', 'scheduledStartAt', 'createdAt'],
};

const EXPLORE_NUMERIC: Record<(typeof EXPLORE_SOURCE_KEYS)[number], readonly string[]> = {
  enrollments_detail: ['totalTimeSpentSeconds', 'completed'],
  groups_overview: ['membersCount'],
  flows_overview: ['enrollments'],
  webinars_list: [],
};

export type ExploreSchemaSource = {
  key: string;
  labelKey: string;
  dimensions: { key: string; labelKey: string }[];
  numericColumns: { key: string; labelKey: string }[];
};

export type InstitutionBiCatalogDataset = {
  key: string;
  labelKey: string;
  descriptionKey: string;
  allowedTypes: string[];
  configHints?: {
    supportsLimit?: boolean;
    limitDefault?: number;
    limitMax?: number;
    supportsColors?: boolean;
    supportsGridSpan?: boolean;
    exploreSchema?: ExploreSchemaSource[];
  };
};

function buildExploreChartEntry(): InstitutionBiCatalogDataset {
  const exploreSchema: ExploreSchemaSource[] = EXPLORE_SOURCE_KEYS.map((src) => ({
    key: src,
    labelKey: `institution.bi.explore.source.${src}`,
    dimensions: EXPLORE_DIMS[src].map((d) => ({ key: d, labelKey: `institution.bi.column.${d}` })),
    numericColumns: EXPLORE_NUMERIC[src].map((d) => ({ key: d, labelKey: `institution.bi.column.${d}` })),
  }));
  return {
    key: 'explore_chart',
    labelKey: 'institution.bi.dataset.explore_chart',
    descriptionKey: 'institution.bi.dataset.explore_chartDesc',
    allowedTypes: ['bar', 'line', 'pie', 'table'],
    configHints: {
      supportsLimit: true,
      limitDefault: 20,
      limitMax: 50,
      supportsColors: true,
      supportsGridSpan: true,
      exploreSchema,
    },
  };
}

/** Insère « Graphique personnalisé » après « Activité sur 8 semaines » si absent. */
export function mergeExploreChartDatasetIfMissing<T extends { key: string }>(datasets: T[]): T[] {
  if (!Array.isArray(datasets) || datasets.some((d) => d.key === 'explore_chart')) {
    return datasets;
  }
  const entry = buildExploreChartEntry() as unknown as T;
  const idx = datasets.findIndex((d) => d.key === 'activity_weeks');
  if (idx === -1) {
    return [...datasets, entry];
  }
  return [...datasets.slice(0, idx + 1), entry, ...datasets.slice(idx + 1)];
}
